import { useCallback } from 'react';

import type { InviteUserResponse } from '@atlassian/invite-people/types';

import { useFlagActions } from '@townsquare/flags';

import { isBulkInviteSuccessResponse } from '../utils/isBulkInviteSuccessResponse';

import { useGetInviteToProjectFlags } from './useGetInviteToProjectFlags';
import type { InviteToProjectResult } from './useInviteToProject';

const collectEmails = <T extends { email?: string }>(users: T[], cb: (email: string, u: T) => void) => {
	for (const u of users) {
		if (u.email) {
			cb(u.email, u);
		}
	}
};

/**
 * Compares project and product invite results and returns:
 * - filteredSuccessfulInvites: project successes, filtered against product successes only when
 *   the product showed a success flag (at least one invited/access-requested)
 * - filteredFailedInvites: project failures not covered by any product result
 * - anomalyProductFailStatuses: error codes for emails that succeeded in project but failed in product
 */
export const getFilteredProjectResults = (
	projectResult: InviteToProjectResult,
	inviteUsersResponse: InviteUserResponse,
) => {
	const productSuccessEmails = new Set<string>();
	const productFailEmails = new Map<string, { code?: string; message?: string }>();

	const data = inviteUsersResponse.status === 'fulfilled' ? inviteUsersResponse.value : null;

	if (data?.responseStatus === 'success' && isBulkInviteSuccessResponse(data.response)) {
		const { invited, accessRequested, error } = data.response;
		collectEmails(invited, email => productSuccessEmails.add(email));
		collectEmails(accessRequested, email => productSuccessEmails.add(email));
		collectEmails(error, (email, u) =>
			productFailEmails.set(email, { code: u.error?.code, message: u.error?.message }),
		);
	} else if (data && data.responseStatus !== 'skipped') {
		// Product failed entirely — it already showed an error flag, so suppress all project flags.
		collectEmails(projectResult.failedInvites, email => productFailEmails.set(email, {}));
		collectEmails(projectResult.successfulInvites, email => productFailEmails.set(email, {}));
	}

	const hasProductSuccessFlag = productSuccessEmails.size > 0;
	const anomalyProductFailStatuses: string[] = [];
	const anomalyProductFailMessages: string[] = [];

	// Detect anomalies and collect non-product-covered successes in a single pass
	const nonProductCoveredSuccesses: typeof projectResult.successfulInvites = [];
	for (const invite of projectResult.successfulInvites) {
		if (!productSuccessEmails.has(invite.email) && productFailEmails.has(invite.email)) {
			const entry = productFailEmails.get(invite.email)!;
			anomalyProductFailStatuses.push(entry.code ?? 'unknown');
			anomalyProductFailMessages.push(entry.message ?? 'unknown');
		}
		if (!hasProductSuccessFlag || !productSuccessEmails.has(invite.email)) {
			nonProductCoveredSuccesses.push(invite);
		}
	}

	// When there's an anomaly, show ALL project successes so the user sees the full
	// project-level picture (e.g. "bob and latte were added to the project" even though
	// bob failed at the product level). Otherwise, only show those not already covered
	// by the product success flag.
	const filteredSuccessfulInvites =
		anomalyProductFailStatuses.length > 0 ? projectResult.successfulInvites : nonProductCoveredSuccesses;

	const filteredFailedInvites = projectResult.failedInvites.filter(f => !productFailEmails.has(f.email));

	return { filteredSuccessfulInvites, filteredFailedInvites, anomalyProductFailStatuses, anomalyProductFailMessages };
};

/**
 * Returns a function that displays flags for project invite outcomes not already
 * covered by the product invite result, and returns anomaly fail statuses for analytics.
 *
 * - Project success flags are shown when the product did not show a success flag, or for
 *   emails not covered by the product success flag.
 * - Project failure flags are shown only for emails not present in product fail results.
 */
export const useHandleInviteFlags = () => {
	const { addFlag } = useFlagActions();
	const getFlags = useGetInviteToProjectFlags();

	return useCallback(
		(
			projectResult: InviteToProjectResult | null,
			inviteUsersResponse: InviteUserResponse,
			isSiteAdmin: boolean,
		): { anomalyFailStatuses: string[]; anomalyFailMessages: string[] } => {
			if (!projectResult) {
				return { anomalyFailStatuses: [], anomalyFailMessages: [] };
			}

			const {
				filteredSuccessfulInvites,
				filteredFailedInvites,
				anomalyProductFailStatuses,
				anomalyProductFailMessages,
			} = getFilteredProjectResults(projectResult, inviteUsersResponse);

			if (filteredSuccessfulInvites.length > 0 || filteredFailedInvites.length > 0) {
				getFlags(
					{
						successfulInvites: filteredSuccessfulInvites,
						failedInvites: filteredFailedInvites,
					},
					isSiteAdmin,
				).forEach(flag => addFlag(flag));
			}

			return {
				anomalyFailStatuses: anomalyProductFailStatuses,
				anomalyFailMessages: anomalyProductFailMessages,
			};
		},
		[addFlag, getFlags],
	);
};
